import { useEffect, useRef, useState } from 'react';
import { withContent } from '@bubbletea/core';
import { pollWindowVariable } from '@mooncake/mooncake-gateway-core/utils';
import uniqueId from 'lodash/uniqueId';
import { FaqItem } from '../../../types/generatedContent';
import { IIbexPricingDetail } from '../../../types/subscription';
import data from '../../../data/content.json';

import { AccordionAnswer, AccordionQuestion, ItemContainer } from './styles';
import { DropDownIcon } from '../../UI/Icons';
import { IContent } from '../../../types';
import sendEvent from '../../../utils/sendEvent';
import findCurrentSubscription from '../../../utils/findCurrentSubscription';

interface IFAQItem {
  context: IContent;
  item: FaqItem;
  index: number;
  isInView: boolean;
}

type TIbexPricingDetails = {
  subscriptions: IIbexPricingDetail[];
};

const FAQItem = ({ context, item, index, isInView }: IFAQItem) => {
  const { products } = context.googlesheet;
  const [active, setActive] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  const [ibexPricingDetails, setIbexPricingDetails] = useState<TIbexPricingDetails>({ subscriptions: [] });

  const contentData = data as IContent[];
  const contentEN = contentData.filter((dataFromContent) => dataFromContent.lang === 'en_US');
  const questionEN = contentEN[0].googlesheet.faq.faqItems[index].question;

  const openQuestion = (event: CustomEvent) => {
    if (parseInt(event.detail) === index + 1) {
      setActive(true);
    }
  };

  useEffect(() => {
    if (window.location.hash) {
      const regex = /#faq(\d+)/;
      const matches = window.location.hash?.match(regex);
      if (matches?.length && parseInt(matches[1]) === index + 1) {
        setActive(true);
      }
    }

    const fetchIbexPricingDetails = async () => {
      const pricingDetails = await pollWindowVariable<TIbexPricingDetails>('window.ibexPricingDetails');
      setIbexPricingDetails(pricingDetails);
    };
    fetchIbexPricingDetails();

    document.addEventListener('OPEN_FAQ_QUESTION', openQuestion as EventListener);

    return () => {
      setIbexPricingDetails({ subscriptions: [] });
      document.removeEventListener('OPEN_FAQ_QUESTION', openQuestion as EventListener);
    };
  }, []);

  const formatAnswerText = (text: string) => {
    const sub1month = findCurrentSubscription(ibexPricingDetails, products, 'oneMonth');
    const sub3month = findCurrentSubscription(ibexPricingDetails, products, 'threeMonths');
    const sub12month = findCurrentSubscription(ibexPricingDetails, products, 'twelveMonths');
    return text
      .replace(/(?:\r\n|\r|\n)/g, '<br />')
      .replace('{price-12month}', sub12month?.subscription?.basePriceFormatted as string)
      .replace('{price-3month}', sub3month?.subscription?.basePriceFormatted as string)
      .replace('{price-1month}', sub1month?.subscription?.basePriceFormatted as string);
  };

  const { answer, question } = item;
  const answers = answer?.map((answerItem) => {
    if (!answerItem) {
      return null;
    }
    if (Array.isArray(answerItem)) {
      const formattedAnswer = answerItem
        .map((answerItemFromArray) => {
          const formattedString = answerItemFromArray ? (answerItemFromArray as string) : null;
          return formattedString ? formatAnswerText(formattedString) : null;
        })
        .join('<br>');
      return <p dangerouslySetInnerHTML={{ __html: formattedAnswer }} />;
    }
    const formattedString = answerItem ? (answerItem as string) : null;
    return formattedString ? (
      <p key={`faq_answer_${uniqueId()}`} dangerouslySetInnerHTML={{ __html: formatAnswerText(formattedString) }} />
    ) : null;
  });

  const openFAQQuestion = () => {
    const eventDetails = {
      location: 'faq',
      category: 'interaction',
      locationPosition: 10,
      action: questionEN,
    };
    sendEvent(eventDetails);
    setActive(!active);
  };

  return (
    <ItemContainer
      initial={{
        opacity: 0,
        y: 3,
      }}
      animate={
        isInView && {
          opacity: 1,
          y: 0,
          transition: {
            type: 'spring',
            delay: 0.1 + index * 0.1,
          },
        }
      }
      id={`faq${index + 1}`}
    >
      <AccordionQuestion
        active={active}
        aria-controls={`accordion-item-${index}`}
        aria-expanded={active}
        type={'button'}
        onClick={() => openFAQQuestion()}
      >
        <span>
          <span>{question}</span>
        </span>
        <DropDownIcon />
      </AccordionQuestion>
      <AccordionAnswer
        active={active}
        ref={content}
        style={{ maxHeight: `${active ? `${content?.current?.scrollHeight}px` : '0px'}` }}
      >
        {answers}
      </AccordionAnswer>
    </ItemContainer>
  );
};

export default withContent(FAQItem);
